import { useMemo, type ReactNode } from 'react'
import type { OpeningType } from '~/components/ThreeDModel/furnitureConfig'
import {
  ColumnConfigurationType,
} from '~/types/columnConfigurationTypes'
import type { ColumnConfigurationWithOptions } from '~/types/furniture3D'
import { usePricingQuoteSync } from '~/utils/pricingQuote'
import { computeStandParts } from '~/utils/standParts'
import { QuoteProvider } from '~/context/pricingQuoteContext'

interface StandQuoteProviderProps {
  sku: string
  chipboardId?: string
  width: number
  height: number
  depth: number
  plintHeight: number
  columns: number
  sections: number
  openingType: OpeningType
  columnWidths?: number[]
  columnConfigurationsWithOptions?: ColumnConfigurationWithOptions[]
  columnConfigurations?: ColumnConfigurationType[]
  children: ReactNode
}

/**
 * Computes the stand's PAL panels and fires a live pricing-quote on every config
 * change, publishing the result through the shared QuoteContext (same one the
 * wardrobe uses) so ProductPrice shows the total + breakdown.
 */
export function StandQuoteProvider({
  sku,
  chipboardId,
  width,
  height,
  depth,
  plintHeight,
  columns,
  sections,
  openingType,
  columnWidths,
  columnConfigurationsWithOptions,
  columnConfigurations,
  children,
}: StandQuoteProviderProps) {
  const { panels, doorsNumber, zones } = useMemo(
    () =>
      computeStandParts({
        width,
        height,
        depth,
        plintHeight,
        columns,
        sections,
        columnWidths,
        columnConfigurationsWithOptions,
        columnConfigurations,
      }),
    [
      width,
      height,
      depth,
      plintHeight,
      columns,
      sections,
      columnWidths,
      columnConfigurationsWithOptions,
      columnConfigurations,
    ]
  )

  const quote = usePricingQuoteSync({
    sku,
    chipboardId,
    width,
    height,
    depth,
    handleType: openingType,
    doorsNumber,
    sections: columns,
    parts: panels,
    zones,
  })

  return <QuoteProvider value={quote}>{children}</QuoteProvider>
}
